import { CSSProperties, useEffect, useState } from "react";

import { Button } from "@blueprintjs/core";
import styled from "@emotion/styled";
import Uppy, { Meta, UppyFile } from "@uppy/core";
import XHRUpload from "@uppy/xhr-upload";

import execWm from "@/../../hypetrain/static/img/exec-wm.png";
import { Box, Flex } from "@/components/layout/flexbox";
import { getJwtToken } from "@/components/session/JwtTokenManager";

import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import "@uppy/url/dist/style.min.css";

interface InlineEditableImageProps {
  allowMultipleUploads?: boolean;
  meta?: Meta;
  onComplete?: () => void;
  onUploadSuccess?: (fileUrl?: string) => void;
  onFileAdded?: (file: File) => void;
  onFileRemoved?: (file: UppyFile<Meta, Record<string, never>>) => void;
  onError?: (error: Error) => void;
  uppyRef?: (uppy: Uppy) => void;
  imageSrc?: string;
  isEditable?: boolean;
  endpoint?: string;
  fieldName?: string;
  isDeletable?: boolean;
  containerStyle?: CSSProperties;
}

export function InlineEditableImage({
  allowMultipleUploads = false,
  meta = {},
  imageSrc,
  isEditable,
  onUploadSuccess,
  containerStyle,
  ...props
}: InlineEditableImageProps) {
  const [uppy, setUppy] = useState<Uppy>();
  const [isHovering, setIsHovering] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(imageSrc);
  const fileInput: HTMLInputElement | null =
    document.querySelector(`#file-input`);

  useEffect(() => {
    setUppy(
      new Uppy({
        allowMultipleUploads: allowMultipleUploads,
        restrictions: {
          maxNumberOfFiles: allowMultipleUploads ? 10 : 1,
          minNumberOfFiles: 1,
        },
        meta: meta,
      }).use(XHRUpload, {
        endpoint: props.endpoint ?? "",
        fieldName: props.fieldName,
        headers: {
          Authorization: `JWT ${getJwtToken()}`,
        },
      }),
    );
    return () => {
      if (uppy) uppy.cancelAll();
    };
  }, []);

  useEffect(() => {
    if (!uppy) return;

    uppy.on("complete", props.onComplete ?? (() => {}));
    uppy.on("upload-success", (file, response) => {
      if (!file) return;

      const body = response?.body as Record<string, ExplicitAny>;

      setTempImageSrc(body.url);
      onUploadSuccess?.(body.url);
      uppy.removeFile(file.id);
    });

    uppy.on("file-removed", props.onFileRemoved ?? (() => {}));
    uppy.on("error", props.onError ?? (() => {}));

    if (props.uppyRef) {
      props.uppyRef(uppy);
    }
  }, [uppy]);

  useEffect(() => {
    setTempImageSrc(imageSrc);
  }, [imageSrc]);

  return (
    <UppyContainer style={containerStyle}>
      <Flex
        id="drag-drop"
        width="100%"
        height="100%"
        alignItems="center"
        onClick={() => {
          if (isEditable && fileInput) {
            fileInput.click();
          }
        }}
        onMouseLeave={() => {
          if (isEditable) {
            setIsHovering(false);
          }
        }}
        onMouseEnter={() => {
          if (isEditable) {
            setIsHovering(true);
          }
        }}
      >
        {tempImageSrc && (
          <>
            <img
              src={tempImageSrc}
              style={{
                objectFit: "contain",
                width: "100%",
                height: "100%",
              }}
            />
            {isEditable && (
              <Flex
                sx={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  gap: "5px",
                }}
              >
                <EditImageButton
                  isHovering={isHovering}
                  isEditable={isEditable}
                  uppy={uppy}
                />

                <Button
                  icon="cross"
                  style={{
                    padding: "4px",
                    display: isHovering ? "" : "none",
                    cursor: isHovering && isEditable ? "pointer" : "default",
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setTempImageSrc(execWm);
                    onUploadSuccess?.();
                  }}
                />
              </Flex>
            )}
          </>
        )}
      </Flex>
    </UppyContainer>
  );
}

interface EditImageButtonProps {
  isHovering: boolean;
  isEditable: boolean;
  uppy?: Uppy;
}

function EditImageButton({
  isHovering,
  isEditable,
  uppy,
}: EditImageButtonProps) {
  const fileInput: HTMLInputElement | null =
    document.querySelector(`#file-input`);

  useEffect(() => {
    fileInput?.addEventListener("change", (event: Event) => {
      const fileList = (event.target as HTMLInputElement).files;
      if (!fileList) return;
      const files = Array.from(fileList);

      files.forEach((file) => {
        try {
          uppy?.addFile({
            source: "file input",
            name: file.name,
            type: file.type,
            data: file,
          });

          uppy?.upload();
        } catch (err: unknown) {
          if (err instanceof Error && "isRestriction" in err) {
            // handle restrictions
            console.log("Restriction error:", err);
          } else {
            console.error(err);
          }
        }
      });
    });

    // Clear the `<input>` after the upload or when the file was removed
    // so the file can be uploaded again (see
    // https://github.com/transloadit/uppy/issues/2640#issuecomment-731034781).
    uppy?.on("file-removed", () => {
      if (fileInput) {
        fileInput.value = "";
      }
    });

    uppy?.on("complete", () => {
      if (fileInput) {
        fileInput.value = "";
      }
    });

    return () => {
      fileInput?.removeEventListener("change", () => {});
    };
  }, [uppy]);

  return (
    <>
      <input
        id="file-input"
        type="file"
        accept="image/*"
        style={{
          display: "none",
        }}
      />
      <Button
        style={{
          padding: "4px",
          display: isHovering ? "" : "none",
          cursor: isHovering && isEditable ? "pointer" : "default",
        }}
        icon="edit"
        onClick={(event) => {
          event.stopPropagation();

          if (fileInput) {
            fileInput.click();
          }
        }}
      />
    </>
  );
}

const UppyContainer = styled(Box)`
  [data-uppy-drag-drop-supported="true"] .uppy-Dashboard-AddFiles {
    border: none;
  }
`;
